import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { championshipApi, registrationApi, athleteApi } from '../lib/api';
// FASE 2 — textos de confirmação em módulo próprio (mesmo aviso em todas as
// telas; funções puras, testáveis sem jsdom).
import { buildRegenerateWarning, buildAwardWarning } from '../lib/warnings';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { EditWeightDialog } from '../components/EditWeightDialog';
import { AbsoluteTab } from '../components/AbsoluteTab';
import { SuperfightsTab } from '../components/SuperfightsTab';
import { PublicAthleteList } from '../components/PublicAthleteList';
import {
  ArrowLeft, Loader2, Trophy, Users, CheckCircle, Award,
  Calendar, MapPin, FileText, Download, Play, Clock, DollarSign,
  Scale, UserPlus, Search, User, CreditCard, Tag, Crown,
  GitBranch, BookOpen, Camera, Heart, CalendarDays, Pencil, Trash2, MonitorPlay,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const verticalTabs = [
  { key: 'registration_values', label: 'Valores das Inscricoes', icon: DollarSign },
  { key: 'payment_methods', label: 'Formas de Pagamento', icon: CreditCard },
  { key: 'prizes', label: 'Premiacao', icon: Award },
  { key: 'categories_info', label: 'Categorias', icon: Tag },
  { key: 'absolute_info', label: 'Absoluto', icon: Crown },
  { key: 'checkin_info', label: 'Checagem', icon: CheckCircle },
  { key: 'brackets_info', label: 'Chaves', icon: GitBranch },
  { key: 'weigh_in_info', label: 'Pesagem', icon: Scale },
  { key: 'rules', label: 'Regras', icon: BookOpen },
  { key: 'image_rights', label: 'Direito de Imagem', icon: Camera },
  { key: 'health_declaration', label: 'Declaracao de Saude', icon: Heart },
  { key: 'schedule', label: 'Cronograma', icon: CalendarDays },
];

// Module-level cache for championship data (survives remounts within the same session)
const championshipDetailCache = new Map();

export default function ChampionshipDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isProfessor = user?.role === 'professor';
  const isAthlete = user?.role === 'athlete';
  const isSuperAdmin = user?.role === 'super_admin';
  const isFederation = user?.role === 'federation';
  const canEnroll = ['super_admin', 'federation', 'professor'].includes(user?.role);
  const [championship, setChampionship] = useState(null);
  const [registrations, setRegistrations] = useState(null);
  const [brackets, setBrackets] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDetailTab, setActiveDetailTab] = useState(null);

  // Editing/deleting is unrestricted for super_admin; federation can only manage own championships
  const canEdit = isSuperAdmin || (isFederation && championship?.created_by === user?._id);

  // Enrollment modal
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [athleteSearch, setAthleteSearch] = useState('');
  const [myAthletes, setMyAthletes] = useState([]);
  const [enrolling, setEnrolling] = useState(null);
  // Self-enroll (professor)
  const [myProfile, setMyProfile] = useState(null);
  const [selfEnrolling, setSelfEnrolling] = useState(false);
  // Modalidade (gi | nogi) usada nas inscricoes — relevante no evento misto
  const [selfEnrollModality, setSelfEnrollModality] = useState('gi');
  const [enrollModality, setEnrollModality] = useState('gi');
  // Bracket athletes popup
  const [selectedBracketAthletes, setSelectedBracketAthletes] = useState(null);
  // Edit weight dialog
  const [editingAthlete, setEditingAthlete] = useState(null);
  // Pagination (Registrations tab)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Load championship from cache if available, avoiding redundant API call
  // when navigating back to Detalhes from another sub-page.
  // Cache is invalidated whenever setChampionship is called with fresh data
  // (e.g. after handleFinalize), so mutations always reflect on next mount.
  useEffect(() => {
    if (championshipDetailCache.has(id)) {
      setChampionship(championshipDetailCache.get(id));
    }
  }, [id]);

  const reloadBracketsAndRegs = async () => {
    try {
      const [regsRes, bracketsRes] = await Promise.all([
        registrationApi.list({ championship_id: id }),
        championshipApi.getBrackets(id),
      ]);
      setRegistrations(regsRes.data);
      setBrackets(bracketsRes.data);
    } catch (e) {
      // toast already shown by interceptor; nothing else to do
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [champRes, regsRes, bracketsRes] = await Promise.all([
          championshipApi.get(id),
          registrationApi.list({ championship_id: id }),
          championshipApi.getBrackets(id)
        ]);
        championshipDetailCache.set(id, champRes.data);
        setChampionship(champRes.data);
        setRegistrations(regsRes.data);
        setBrackets(bracketsRes.data);
        // Auto-select first detail tab with content
        const firstTab = verticalTabs.find(t => champRes.data[t.key]);
        setActiveDetailTab(firstTab ? firstTab.key : verticalTabs[0].key);
        // Load professor's athlete profile for self-enrollment
        if (isProfessor) {
          try {
            const { data } = await athleteApi.myProfile();
            setMyProfile(data);
          } catch (e) { /* no profile */ }
        }
      } catch (error) {
        toast.error('Erro ao carregar campeonato');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleGenerateBrackets = async () => {
    try {
      const { data } = await championshipApi.generateBrackets(id);
      toast.success('Chaves geradas com sucesso!');
      // FASE D5 — blindagem: aviso de faixa não reconhecida (nunca bloqueia).
      (data?.warnings || []).forEach((w) => toast.warning(w));
      await reloadBracketsAndRegs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar chaves');
    }
  };

  // FASE 1.3 + FASE 2 — o aviso de regerar chaves passa a ser EXPLÍCITO quando o
  // campeonato já tem resultado lançado ou medalha registrada. A checagem é
  // somente leitura e o TEXTO vive em `lib/warnings` (o mesmo aviso é usado em
  // todas as telas e pode ser testado sem jsdom). Se a checagem falhar, o aviso
  // básico continua valendo — nunca bloqueia a ação nem muda o fluxo da tela.
  const buildRegenerateWarningText = async () => {
    let colocacoes = 0;
    let medalhas = 0;
    try {
      const [rankingRes, medalsRes] = await Promise.all([
        championshipApi.getLiveRanking(id),
        championshipApi.getMedals(id),
      ]);
      colocacoes = (rankingRes?.data || []).length;
      medalhas = (medalsRes?.data?.medals || []).length;
    } catch (e) {
      // Checagem indisponível: mantém o aviso básico (comportamento anterior).
    }
    return buildRegenerateWarning({ colocacoes, medalhas });
  };

  const handleRegenerateBrackets = async () => {
    const ok = window.confirm(await buildRegenerateWarningText());
    if (!ok) return;
    try {
      const { data } = await championshipApi.generateBrackets(id);
      toast.success('Chaves atualizadas com a lista atual de atletas');
      // FASE D5 — blindagem: o backend avisa quando encontra faixa que não
      // conhece (ela formaria categoria própria, sem tempo de luta/validação).
      (data?.warnings || []).forEach((w) => toast.warning(w));
      await reloadBracketsAndRegs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar chaves');
    }
  };

  const handleDeleteAllBrackets = async () => {
    const ok = window.confirm(
      'EXCLUIR TODAS AS CHAVES?\n\n' +
      'Esta acao ira remover PERMANENTEMENTE todas as chaves deste campeonato, ' +
      'incluindo placares, vencedores e historico das lutas.\n\n' +
      'As inscricoes dos atletas serao mantidas. Voce podera gerar novas chaves depois.\n\n' +
      'Deseja continuar?'
    );
    if (!ok) return;
    try {
      const { data } = await championshipApi.deleteAllBrackets(id);
      toast.success(`${data.deleted_count || 0} chaves excluidas com sucesso`);
      await reloadBracketsAndRegs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir chaves');
    }
  };

  // Detect athletes that were confirmed AFTER the brackets were created and
  // therefore aren't part of any existing match — surfaces the staleness so
  // the user knows to refresh.
  const athletesInBrackets = React.useMemo(() => {
    const set = new Set();
    (brackets || []).forEach((b) =>
      b.matches?.forEach((m) => {
        if (m.athlete1_id) set.add(m.athlete1_id);
        if (m.athlete2_id) set.add(m.athlete2_id);
      }),
    );
    return set;
  }, [brackets]);

  const missingFromBrackets = (registrations || []).filter(
    (r) =>
      r.payment_status === 'confirmed' &&
      !r.is_absolute &&
      !athletesInBrackets.has(r.athlete_id),
  ).length;

  const handleFinalize = async () => {
    // FASE 2.1 — a premiação é o evento que GRAVA as medalhas (o rótulo
    // "Finalizado" não premia). O aviso diz exatamente isso e lembra que chaves
    // de Lançamento Direto são puladas.
    if (!window.confirm(buildAwardWarning())) return;
    try {
      const { data } = await championshipApi.finalize(id);
      // FASE 7.1 — aviso explícito quando as medalhas NÃO entram no Ranking
      // Federativo (campeonato sem federação/snapshot): nunca mais silencioso.
      const ranking = data?.ranking_federativo;
      if (ranking && ranking.processed === false) {
        toast.warning(
          ranking.message
            || 'Medalhas gravadas, mas o Ranking Federativo NÃO foi atualizado.',
        );
      } else {
        toast.success('Campeonato finalizado!');
      }
      const champRes = await championshipApi.get(id);
      championshipDetailCache.set(id, champRes.data);
      setChampionship(champRes.data);
    } catch (error) { toast.error('Erro ao finalizar campeonato'); }
  };

  const openEnrollDialog = async () => {
    setShowEnrollDialog(true);
    try {
      const { data } = await athleteApi.list();
      setMyAthletes(data);
    } catch (e) { toast.error('Erro ao carregar atletas'); }
  };

  const handleSelfEnroll = async () => {
    if (!myProfile) {
      toast.error('Voce nao possui cadastro de atleta');
      return;
    }
    setSelfEnrolling(true);
    try {
      await registrationApi.create({
        championship_id: id,
        athlete_id: myProfile._id,
        weight_category: myProfile.age_class || '',
        is_absolute: false,
        modality: selfEnrollModality
      });
      toast.success('Voce foi inscrito com sucesso!');
      const regsRes = await registrationApi.list({ championship_id: id });
      setRegistrations(regsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao se inscrever');
    } finally { setSelfEnrolling(false); }
  };

  const handleEnroll = async (athlete) => {
    setEnrolling(athlete._id);
    try {
      await registrationApi.create({
        championship_id: id,
        athlete_id: athlete._id,
        weight_category: athlete.age_class || '',
        is_absolute: false,
        modality: enrollModality
      });
      toast.success(`${athlete.name} inscrito com sucesso!`);
      const regsRes = await registrationApi.list({ championship_id: id });
      setRegistrations(regsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao inscrever');
    } finally { setEnrolling(null); }
  };

  const handleConfirmPayment = async (regId) => {
    try {
      await registrationApi.confirm(regId);
      toast.success('Pagamento confirmado');
      const regsRes = await registrationApi.list({ championship_id: id });
      setRegistrations(regsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao confirmar pagamento');
    }
  };

  const handleDeleteRegistration = async (regId) => {
    if (!window.confirm('Excluir esta inscricao?')) return;
    try {
      await registrationApi.delete(regId);
      toast.success('Inscricao excluida');
      const regsRes = await registrationApi.list({ championship_id: id });
      setRegistrations(regsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir inscricao');
    }
  };

  const filteredMyAthletes = myAthletes.filter(a =>
    athleteSearch.length === 0 || a.name.toLowerCase().includes(athleteSearch.toLowerCase())  );

  const getBracketAthletes = (bracket) => {
    const athletes = new Map();
    bracket.matches?.forEach(m => {
      if (m.athlete1_id && m.athlete1_name) athletes.set(m.athlete1_id, m.athlete1_name);
      if (m.athlete2_id && m.athlete2_name) athletes.set(m.athlete2_id, m.athlete2_name);
    });
    // Enrich each athlete with their registration data (for inline weight edit)
    const regByAthlete = new Map((registrations || []).map(r => [r.athlete_id, r]));
    return Array.from(athletes.entries()).map(([athId, name]) => {
      const reg = regByAthlete.get(athId);
      return {
        id: athId,
        name,
        registration_id: reg?._id,
        weight: reg?.athlete_weight,
        weight_category: reg?.weight_category,
        sex: reg?.athlete_sex,
        age_class: reg?.athlete_age_class,
        manually_moved: reg?.manually_moved,
        original_age_class: reg?.original_age_class,
      };
    });
  };

  if (loading && !championship) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" aria-hidden="true" /><span role="status" aria-live="polite" className="ml-2 text-sm text-[#A1A1AA]">Carregando...</span></div>;
  }
  if (!championship) {
    return <div className="text-center py-16"><Trophy className="w-16 h-16 text-[#71717A] mx-auto mb-4" /><h2 className="text-xl font-bold text-white">Campeonato nao encontrado</h2></div>;
  }

  const statusLabels = { open: 'Inscricoes Abertas', closed: 'Inscricoes Encerradas', in_progress: 'Em Andamento', finished: 'Finalizado' };
  const confirmedCount = (registrations || []).filter(r => r.payment_status === 'confirmed').length;
  const c = championship;
  const isMisto = (c?.modality || 'gi') === 'misto';

  // Inscrito por modalidade: em evento Misto, a inscricao so "conta" para a
  // modalidade selecionada (Kimono e No-Gi sao inscricoes separadas).
  const enrolledIdsFor = (modality) =>
    new Set(
      (registrations || [])
        .filter(r => !isMisto || (r.modality || 'gi') === modality)
        .map(r => r.athlete_id)
    );

  // ---------- Client-side pagination (Registrations tab) ----------
  const totalPages = Math.max(1, Math.ceil((registrations || []).length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (registrations || []).length === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, (registrations || []).length);
  const pagedRegistrations = (registrations || []).slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const renderPaginationBar = (showPageSize) => (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-3">
      <div className="flex flex-wrap items-center gap-3 text-sm text-[#A1A1AA]">
        {showPageSize && (
          <>
            <span className="text-xs uppercase tracking-wider text-[#71717A]">Exibir por pagina</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
              <SelectTrigger data-testid="page-size-select" className="bg-[#1A1A1A] border-[#27272A] text-white h-8 w-[76px]"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                {[25, 50, 75, 100].map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-white hover:bg-[#27272A]">{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        )}
        <span>Mostrando <span className="text-white font-semibold">{start}-{end}</span> de <span className="text-white font-semibold">{registrations.length}</span> inscricoes</span>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="prev-page" className="border-[#27272A] text-white">
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="px-2 text-sm text-[#A1A1AA]">Pagina <span className="text-white font-semibold">{currentPage}</span> / {totalPages}</span>
        <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="next-page" className="border-[#27272A] text-white">
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  // Check if there are any detail fields
  const hasDetails = c.about_event || c.event_location || c.registration_values || c.payment_methods || c.prizes || c.categories_info || c.absolute_info || c.checkin_info || c.brackets_info || c.weigh_in_info || c.rules || c.image_rights || c.health_declaration || c.schedule;

  return (
      <div className="space-y-6" data-testid="championship-detail">
        {/* Header */}
        <div className="flex items-start gap-4">
          <Button variant="ghost" onClick={() => navigate('/championships')} className="text-[#A1A1AA] hover:text-white mt-1"><ArrowLeft size={20} /></Button>
          <div className="flex-1">
            {/* Trilha de contexto (Etapa Visual 2) — responde "onde estou"
                sem precisar ler o nome do evento. */}
            <nav aria-label="Você está em" className="mb-2 flex flex-wrap items-center gap-1.5 text-xs text-[#71717A]">
              <Link to="/championships" className="transition-colors hover:text-[#D4AF37]">Campeonatos</Link>
              <ChevronRight size={12} aria-hidden="true" className="opacity-60" />
              <span className="truncate">{c.name}</span>
            </nav>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-white font-['Chivo']">{c.name}</h1>
              <span className={`px-3 py-1 rounded-full text-sm font-medium status-${c.status}`}>{statusLabels[c.status]}</span>
              {c.draw_mode && (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/40" data-testid="championship-draw-badge">
                  {c.draw_mode === 'random' ? 'Sorteio Aleatório' : 'Sorteio por Critérios'}
                </span>
              )}
              {c.results_mode && (
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium border ${
                    c.results_mode === 'automatic'
                      ? 'bg-green-500/10 text-green-400 border-green-500/40'
                      : c.results_mode === 'hybrid'
                        ? 'bg-purple-500/10 text-purple-400 border-purple-500/40'
                        : 'bg-[#27272A] text-[#A1A1AA] border-[#3F3F46]'
                  }`}
                  data-testid="championship-mode-badge"
                >
                  {c.results_mode === 'automatic' ? 'Placar Automático' : c.results_mode === 'hybrid' ? 'Modo Híbrido' : 'Modo Manual'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-4 text-[#A1A1AA]">
              <span className="flex items-center gap-2"><Calendar size={16} className="text-[#D4AF37]" />{c.date}</span>
              <span className="flex items-center gap-2"><MapPin size={16} className="text-[#D4AF37]" />{c.address}</span>
            </div>
          </div>
          <div className="flex gap-2">
            {isProfessor && c.status === 'open' && myProfile && registrations !== null && (
              <div className="flex items-center gap-2">
                {isMisto && (
                  <Select value={selfEnrollModality} onValueChange={setSelfEnrollModality}>
                    <SelectTrigger data-testid="self-enroll-modality" className="w-[120px] bg-[#1A1A1A] border-[#27272A] text-white h-9 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      <SelectItem value="gi" className="text-white hover:bg-[#27272A]">Kimono</SelectItem>
                      <SelectItem value="nogi" className="text-white hover:bg-[#27272A]">No-Gi</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <Button
                  onClick={handleSelfEnroll}
                  disabled={selfEnrolling || enrolledIdsFor(selfEnrollModality).has(myProfile._id)}
                  className={enrolledIdsFor(selfEnrollModality).has(myProfile._id) ? 'bg-green-800 text-green-200 cursor-default' : 'bg-blue-600 text-white hover:bg-blue-700'}
                  data-testid="self-enroll-btn"
                >
                  {selfEnrolling ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <User size={16} className="mr-2" />}
                  {enrolledIdsFor(selfEnrollModality).has(myProfile._id) ? 'Inscrito' : 'Me Inscrever'}
                </Button>
              </div>
            )}
            {canEnroll && c.status === 'open' && (
              <Button onClick={openEnrollDialog} className="bg-green-600 text-white hover:bg-green-700" data-testid="enroll-athlete-btn">
                <UserPlus size={16} className="mr-2" /> Inscrever Atleta
              </Button>
            )}
            {['super_admin', 'federation', 'staff', 'central_desk'].includes(user?.role) && (
              <Button onClick={() => navigate(`/placar?championship=${id}`)} data-testid="open-scoreboard-btn" className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                <MonitorPlay size={16} className="mr-2" /> Abrir Placar
              </Button>
            )}
            {canEdit && (
              <Button onClick={() => navigate(`/championships/${id}/edit`)} variant="outline" className="border-[#27272A] text-[#A1A1AA] hover:text-white">Editar</Button>
            )}
            {canEdit && c.status === 'in_progress' && (
              <Button onClick={handleFinalize} className="bg-green-600 text-white hover:bg-green-700"><CheckCircle size={20} className="mr-2" />Finalizar</Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center"><Users className="w-6 h-6 text-blue-500" /></div><div><p className="text-[#71717A] text-sm">Inscritos</p><p className="text-2xl font-bold text-white">{registrations === null ? <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin inline" /> : registrations.length}</p></div></CardContent></Card>
          <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center"><CheckCircle className="w-6 h-6 text-green-500" /></div><div><p className="text-[#71717A] text-sm">Confirmados</p><p className="text-2xl font-bold text-white">{registrations === null ? <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin inline" /> : confirmedCount}</p></div></CardContent></Card>
          <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><Award className="w-6 h-6 text-[#D4AF37]" /></div><div><p className="text-[#71717A] text-sm">Chaves</p><p className="text-2xl font-bold text-white">{brackets === null ? <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin inline" /> : brackets.length}</p></div></CardContent></Card>
          <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-4 flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center"><Trophy className="w-6 h-6 text-purple-500" /></div><div><p className="text-[#71717A] text-sm">Categorias</p><p className="text-2xl font-bold text-white">{brackets === null ? <Loader2 className="w-5 h-5 text-[#D4AF37] animate-spin inline" /> : brackets.length}</p></div></CardContent></Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="details" className="w-full">
          <TabsList className="bg-[#141414] border border-[#27272A] p-1 flex-wrap h-auto">
            <TabsTrigger value="details" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Detalhes</TabsTrigger>
            <TabsTrigger value="registrations" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Inscricoes {registrations !== null ? `(${registrations.length})` : ''}</TabsTrigger>
            <TabsTrigger value="athletes-registered" data-testid="tab-athletes-registered" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Atletas Inscritos</TabsTrigger>
            <TabsTrigger value="brackets" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Chaves {brackets !== null ? `(${brackets.filter((b) => !b.is_absolute).length})` : ''}</TabsTrigger>
            {!isAthlete && (
              <TabsTrigger value="absolute" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Absoluto {brackets !== null ? `(${brackets.filter((b) => b.is_absolute).length})` : ''}</TabsTrigger>
            )}
            {!isAthlete && (
              <TabsTrigger value="superfights" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Lutas Casadas</TabsTrigger>
            )}
            {!isAthlete && (
              <TabsTrigger value="reports" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">Relatorios</TabsTrigger>
            )}
          </TabsList>

          {/* Details Tab - Vertical Tabs Layout */}
          <TabsContent value="details" className="mt-6">
            {!hasDetails ? (
              <Card className="bg-[#141414] border-[#27272A]"><CardContent className="text-center py-12 text-[#71717A]"><FileText className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>Nenhum detalhe cadastrado</p>{canEdit && <Link to={`/championships/${id}/edit`} className="text-[#D4AF37] hover:text-[#E5C158] text-sm mt-2 inline-block">Editar campeonato para adicionar detalhes</Link>}</CardContent></Card>
            ) : (
              <div className="flex flex-col lg:flex-row gap-0 rounded-xl overflow-hidden border border-[#27272A] bg-[#141414]" data-testid="detail-vertical-tabs">
                {/* Vertical Sidebar Tabs */}
                <div className="lg:w-60 shrink-0 border-b lg:border-b-0 lg:border-r border-[#27272A] bg-[#0A0A0A]">
                  <div className="flex lg:flex-col overflow-x-auto lg:overflow-x-visible">
                    {verticalTabs.map((tab) => {
                      const isActive = activeDetailTab === tab.key;
                      const hasContent = !!c[tab.key];
                      const TabIcon = tab.icon;
                      return (
                        <button
                          key={tab.key}
                          onClick={() => setActiveDetailTab(tab.key)}
                          data-testid={`vtab-${tab.key}`}
                          className={`flex items-center gap-3 px-4 py-3 text-left text-sm whitespace-nowrap lg:whitespace-normal transition-all w-full ${
                            isActive
                              ? 'bg-[#D4AF37] text-black font-bold'
                              : hasContent
                                ? 'text-white hover:bg-[#141414]'
                                : 'text-[#71717A] hover:bg-[#141414]/50'
                          }`}
                        >
                          <TabIcon size={16} className={isActive ? 'text-black' : hasContent ? 'text-[#D4AF37]' : 'text-[#71717A]'} />
                          <span className="truncate">{tab.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {/* Tab Content */}
                <div className="flex-1 p-6 min-h-[350px]">
                  {(() => {
                    const activeInfo = verticalTabs.find(t => t.key === activeDetailTab);
                    const content = c[activeDetailTab] || '';
                    if (!activeInfo) return null;
                    return (
                      <>
                        <div className="flex items-center gap-3 mb-5">
                          <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                            <activeInfo.icon size={16} className="text-[#D4AF37]" />
                          </div>
                          <h3 className="text-lg font-bold text-white font-['Chivo']">{activeInfo.label}</h3>
                        </div>
                        {content ? (
                          <div className="text-[#A1A1AA] whitespace-pre-wrap leading-relaxed text-sm" data-testid="vtab-content">{content}</div>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-12 text-center">
                            <activeInfo.icon size={36} className="text-[#27272A] mb-3" />
                            <p className="text-[#71717A] text-sm">Informacao nao cadastrada.</p>
                            {canEdit && <Link to={`/championships/${id}/edit`} className="text-[#D4AF37] hover:text-[#E5C158] text-xs mt-2">Editar campeonato</Link>}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Registrations Tab */}
          <TabsContent value="registrations" className="mt-6">
            <Card className="bg-[#141414] border-[#27272A]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white font-['Chivo']">Lista de Inscritos</CardTitle>
                <div className="flex gap-2">
                  {canEnroll && c.status === 'open' && (
                    <Button onClick={openEnrollDialog} className="bg-green-600 text-white hover:bg-green-700" data-testid="enroll-from-tab">
                      <UserPlus size={16} className="mr-2" /> Inscrever
                    </Button>
                  )}
                  {canEdit && (
                    <Link to={`/registrations?championship_id=${id}`}>
                      <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158]">Gerenciar</Button>
                    </Link>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {(registrations || []).length === 0 ? (
                  <div className="text-center py-8 text-[#71717A]"><Users className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>Nenhuma inscricao ainda</p></div>
                ) : (
                  <>
                    <div className="border-b border-[#27272A]/60">{renderPaginationBar(true)}</div>
                    <div className="overflow-x-auto">
                    <table className="tabela w-full">
                      <thead><tr className="border-b border-[#27272A]">
                        <th className="text-left py-3 px-4 text-[#D4AF37] font-medium">Atleta</th>
                        <th className="text-left py-3 px-4 text-[#D4AF37] font-medium">Faixa</th>
                        <th className="text-left py-3 px-4 text-[#D4AF37] font-medium">Categoria</th>
                        <th className="text-left py-3 px-4 text-[#D4AF37] font-medium">Status</th>
                        {!isAthlete && <th className="text-right py-3 px-4 text-[#D4AF37] font-medium">Acoes</th>}
                      </tr></thead>
                      <tbody>
                        {pagedRegistrations.map((reg) => (
                          <tr key={reg._id} className="border-b border-[#27272A]/50 hover:bg-[#1A1A1A]">
                            <td className="py-3 px-4 text-white">{reg.athlete_name}</td>
                            <td className="py-3 px-4"><span className={`px-2 py-1 rounded text-xs capitalize belt-${reg.athlete_belt}`}>{reg.athlete_belt}</span></td>
                            <td className="py-3 px-4 text-[#A1A1AA]">{reg.athlete_age_class}</td>
                            <td className="py-3 px-4"><span className={`px-2 py-1 rounded text-xs status-${reg.payment_status}`}>{reg.payment_status === 'confirmed' ? 'Confirmado' : 'Pendente'}</span></td>
                            {!isAthlete && (
                              <td className="py-3 px-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  {reg.payment_status !== 'confirmed' && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleConfirmPayment(reg._id)}
                                      data-testid={`confirm-registration-${reg._id}`}
                                      className="border-green-500/50 text-green-500 hover:bg-green-500/10"
                                    >
                                      Confirmar
                                    </Button>
                                  )}
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => handleDeleteRegistration(reg._id)}
                                    data-testid={`delete-registration-${reg._id}`}
                                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                    <div className="border-t border-[#27272A]/60">{renderPaginationBar(false)}</div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Atletas Inscritos Tab — reuses PublicAthleteList for logged-in users */}
          <TabsContent value="athletes-registered" data-testid="tab-content-athletes-registered" className="mt-6">
            <PublicAthleteList championshipId={id} />
          </TabsContent>

          {/* Brackets Tab */}
          <TabsContent value="brackets" className="mt-6">
            {(brackets || []).length > 0 && missingFromBrackets > 0 && !isAthlete && (
              <Card className="bg-[#D4AF37]/10 border-[#D4AF37]/40 mb-4" data-testid="brackets-outdated-banner">
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="text-sm text-[#D4AF37]">
                    <strong>{missingFromBrackets}</strong> atleta(s) confirmado(s) ainda não estão nas chaves.
                    Clique em <strong>Atualizar Chaves</strong> para incluí-los.
                  </div>
                  <Button
                    onClick={handleRegenerateBrackets}
                    size="sm"
                    className="bg-[#D4AF37] text-black hover:bg-[#E5C158] shrink-0"
                    data-testid="regenerate-brackets-banner-btn"
                  >
                    <Play size={16} className="mr-1" /> Atualizar Chaves
                  </Button>
                </CardContent>
              </Card>
            )}
            <Card className="bg-[#141414] border-[#27272A]">
              <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-white font-['Chivo']">Chaves</CardTitle>
                <div className="flex flex-wrap gap-2">
                  {(brackets || []).length === 0 && confirmedCount >= 2 && (
                    <Button onClick={handleGenerateBrackets} className="bg-[#D4AF37] text-black hover:bg-[#E5C158]" data-testid="generate-brackets-btn"><Play size={20} className="mr-2" />Gerar Chaves</Button>
                  )}
                  {(brackets || []).length > 0 && !isAthlete && (
                    <>
                      <Button
                        onClick={handleRegenerateBrackets}
                        size="sm"
                        variant="outline"
                        className={missingFromBrackets > 0
                          ? 'border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black animate-pulse'
                          : 'border-[#27272A] text-[#A1A1AA] hover:text-white'}
                        data-testid="regenerate-brackets-btn"
                      >
                        <Play size={16} className="mr-1" />
                        Atualizar Chaves
                        {missingFromBrackets > 0 && (
                          <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-[#D4AF37] text-black text-micro font-bold">
                            +{missingFromBrackets}
                          </span>
                        )}
                      </Button>
                      <a href={championshipApi.getBracketsPdf(id)} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="border-[#27272A] text-[#A1A1AA] hover:text-white" data-testid="print-general-all"><FileText size={16} className="mr-1" />Chave Geral (PDF)</Button>
                      </a>
                      <a href={championshipApi.getStaffTemplatePdf(id)} target="_blank" rel="noopener noreferrer">
                        <Button variant="outline" size="sm" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black" data-testid="staff-template-pdf-all"><FileText size={16} className="mr-1" />Staff Chave (todas)</Button>
                      </a>
                      {isMisto && (
                        <>
                          <a href={championshipApi.getStaffTemplatePdf(id, null, 'gi')} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm" className="border-[#27272A] text-[#A1A1AA] hover:text-white" data-testid="staff-template-pdf-gi"><FileText size={16} className="mr-1" />Staff Chave GI</Button>
                          </a>
                          <a href={championshipApi.getStaffTemplatePdf(id, null, 'nogi')} target="_blank" rel="noopener noreferrer">
                            <Button variant="outline" size="sm" className="border-[#27272A] text-[#A1A1AA] hover:text-white" data-testid="staff-template-pdf-nogi"><FileText size={16} className="mr-1" />Staff Chave No-Gi</Button>
                          </a>
                        </>
                      )}
                      {canEdit && (
                        <Button
                          onClick={handleDeleteAllBrackets}
                          variant="outline"
                          size="sm"
                          className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                          data-testid="delete-all-brackets-btn"
                          title="Excluir todas as chaves deste campeonato"
                        >
                          <Trash2 size={16} className="mr-1" />
                          Excluir Todas as Chaves
                        </Button>
                      )}
                    </>
                  )}
                  <Link to={`/central-desk?championship_id=${id}`}><Button variant="outline" size="sm" className="border-[#27272A] text-[#A1A1AA] hover:text-white">Mesa Central</Button></Link>
                </div>
              </CardHeader>
              <CardContent>
                {(brackets || []).length === 0 ? (
                  <div className="text-center py-8 text-[#71717A]"><Award className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>Nenhuma chave gerada ainda</p><p className="text-sm mt-2">Necessario ter pelo menos 2 atletas confirmados</p></div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {brackets
                      .filter((b) => !b.is_absolute)
                      .sort((a, b) => (a.official_number || 0) - (b.official_number || 0))
                      .map((bracket) => (
                      <div key={bracket._id} className="p-4 rounded-lg bg-[#1A1A1A] border border-[#27272A] hover:border-[#D4AF37]/50 transition-colors" data-testid={`bracket-card-${bracket._id}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center"><Award className="w-5 h-5 text-[#D4AF37]" /></div>
                          <div>
                            <h3 className="font-medium text-white">{bracket.category_name}</h3>
                            <button
                              onClick={() => setSelectedBracketAthletes({ name: bracket.category_name, athletes: getBracketAthletes(bracket) })}
                              className="text-xs text-[#D4AF37] hover:text-[#E5C158] underline underline-offset-2 cursor-pointer"
                              data-testid={`bracket-athletes-link-${bracket._id}`}
                            >
                              {bracket.num_athletes} atletas
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm mb-3"><span className="text-[#A1A1AA]">{bracket.rounds} rodadas</span><span className={`px-2 py-1 rounded text-xs ${bracket.status === 'finished' ? 'status-finished' : bracket.status === 'single_athlete' ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40' : 'status-open'}`}>{bracket.status === 'finished' ? 'Finalizado' : bracket.status === 'single_athlete' ? '⚠ 1 atleta' : 'Em andamento'}</span></div>
                        {!isAthlete && (
                          <div className="flex gap-1">
                            <a href={championshipApi.getBracketsPdf(id, bracket._id)} target="_blank" rel="noopener noreferrer" className="flex-1">
                              <Button variant="outline" size="sm" className="w-full border-[#27272A] text-[#71717A] hover:text-white text-xs" data-testid={`print-general-${bracket._id}`}>
                                <Download size={12} className="mr-1" /> Geral PDF
                              </Button>
                            </a>
                            <a href={championshipApi.getStaffTemplatePdf(id, bracket._id)} target="_blank" rel="noopener noreferrer" className="flex-1">
                              <Button variant="outline" size="sm" className="w-full border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black text-xs" data-testid={`staff-template-pdf-${bracket._id}`}>
                                <FileText size={12} className="mr-1" /> Staff Chave
                              </Button>
                            </a>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Absolute Tab */}
          {!isAthlete && (
            <TabsContent value="absolute" className="mt-6">
              <AbsoluteTab
                championshipId={id}
                brackets={brackets}
                isAthlete={isAthlete}
                onBracketsChanged={reloadBracketsAndRegs}
                modality={c.modality}
              />
            </TabsContent>
          )}

          {/* Superfights Tab */}
          {!isAthlete && (
            <TabsContent value="superfights" className="mt-6">
              <SuperfightsTab championshipId={id} />
            </TabsContent>
          )}

          {/* Reports Tab */}
          <TabsContent value="reports" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center"><FileText className="w-6 h-6 text-red-500" /></div><div className="flex-1"><h3 className="font-bold text-white">Chave Geral (PDF)</h3><p className="text-sm text-[#71717A]">Todas as lutas por categoria</p></div><a href={championshipApi.getBracketsPdf(id)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-[#1A1A1A] text-[#A1A1AA] hover:text-white" data-testid="reports-tab-brackets-pdf" aria-label="Baixar Chave Geral (PDF)"><Download size={20} /></a></div></CardContent></Card>
              <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><FileText className="w-6 h-6 text-[#D4AF37]" /></div><div className="flex-1"><h3 className="font-bold text-white">Staff Chave (todas)</h3><p className="text-sm text-[#71717A]">Template visual preenchido (mala direta)</p></div><a href={championshipApi.getStaffTemplatePdf(id)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-[#1A1A1A] text-[#A1A1AA] hover:text-white" data-testid="reports-tab-staff-template-pdf" aria-label="Baixar Staff Chave (todas) (PDF)"><Download size={20} /></a></div></CardContent></Card>
              {isMisto && (
                <>
                  <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><FileText className="w-6 h-6 text-[#D4AF37]" /></div><div className="flex-1"><h3 className="font-bold text-white">Staff Chave GI</h3><p className="text-sm text-[#71717A]">Somente chaves de Kimono</p></div><a href={championshipApi.getStaffTemplatePdf(id, null, 'gi')} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-[#1A1A1A] text-[#A1A1AA] hover:text-white" data-testid="reports-tab-staff-template-pdf-gi" aria-label="Baixar Staff Chave GI (PDF)"><Download size={20} /></a></div></CardContent></Card>
                  <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center"><FileText className="w-6 h-6 text-[#D4AF37]" /></div><div className="flex-1"><h3 className="font-bold text-white">Staff Chave No-Gi</h3><p className="text-sm text-[#71717A]">Somente chaves de No-Gi</p></div><a href={championshipApi.getStaffTemplatePdf(id, null, 'nogi')} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-[#1A1A1A] text-[#A1A1AA] hover:text-white" data-testid="reports-tab-staff-template-pdf-nogi" aria-label="Baixar Staff Chave No-Gi (PDF)"><Download size={20} /></a></div></CardContent></Card>
                </>
              )}
              <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center"><FileText className="w-6 h-6 text-green-500" /></div><div className="flex-1"><h3 className="font-bold text-white">Lista de Atletas (Excel)</h3><p className="text-sm text-[#71717A]">Exportar inscritos</p></div><a href={championshipApi.getAthletesExcel(id)} target="_blank" rel="noopener noreferrer" aria-label="Baixar Lista de Atletas (Excel)" className="p-2 rounded-lg bg-[#1A1A1A] text-[#A1A1AA] hover:text-white"><Download size={20} /></a></div></CardContent></Card>
              <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center"><FileText className="w-6 h-6 text-orange-500" /></div><div className="flex-1"><h3 className="font-bold text-white">Atletas Sozinhos na Chave (PDF)</h3><p className="text-sm text-[#71717A]">Atletas sem adversário na chave</p></div><a href={championshipApi.getSingleAthleteBracketsPdf(id)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-[#1A1A1A] text-[#A1A1AA] hover:text-white" data-testid="reports-tab-single-athlete-pdf" aria-label="Baixar Atletas Sozinhos na Chave (PDF)"><Download size={20} /></a></div></CardContent></Card>
              <Card className="bg-[#141414] border-[#27272A]"><CardContent className="p-6"><div className="flex items-center gap-4"><div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center"><FileText className="w-6 h-6 text-orange-500" /></div><div className="flex-1"><h3 className="font-bold text-white">Atletas Sozinhos na Chave (Excel)</h3><p className="text-sm text-[#71717A]">Atletas sem adversário na chave</p></div><a href={championshipApi.getSingleAthleteBracketsExcel(id)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg bg-[#1A1A1A] text-[#A1A1AA] hover:text-white" data-testid="reports-tab-single-athlete-excel" aria-label="Baixar Atletas Sozinhos na Chave (Excel)"><Download size={20} /></a></div></CardContent></Card>
            </div>
          </TabsContent>
        </Tabs>

        {/* Enroll Athlete Dialog */}
        <Dialog open={showEnrollDialog} onOpenChange={setShowEnrollDialog}>
          <DialogContent className="bg-[#141414] border-[#27272A] max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-white font-['Chivo']">Inscrever Atleta - {c.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              {isMisto && (
                <div className="flex items-center gap-2">
                  <Label className="text-[#A1A1AA] text-sm">Modalidade</Label>
                  <Select value={enrollModality} onValueChange={setEnrollModality}>
                    <SelectTrigger data-testid="enroll-modality" className="w-[140px] bg-[#1A1A1A] border-[#27272A] text-white h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                      <SelectItem value="gi" className="text-white hover:bg-[#27272A]">Kimono</SelectItem>
                      <SelectItem value="nogi" className="text-white hover:bg-[#27272A]">No-Gi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" size={16} />
                <Input
                  placeholder="Buscar atleta pelo nome..."
                  value={athleteSearch}
                  onChange={(e) => setAthleteSearch(e.target.value)}
                  data-testid="enroll-search"
                  className="pl-10 bg-[#1A1A1A] border-[#27272A] text-white"
                  autoFocus
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto mt-3 space-y-1 min-h-0" data-testid="enroll-athlete-list">
              {filteredMyAthletes.length === 0 ? (
                <p className="text-center text-[#71717A] py-8">Nenhum atleta encontrado</p>
              ) : (
                filteredMyAthletes.map((a) => {
                  const isEnrolled = enrolledIdsFor(enrollModality).has(a._id);
                  return (
                    <div key={a._id} className={`flex items-center justify-between p-3 rounded-lg ${isEnrolled ? 'bg-green-900/10 border border-green-800/30' : 'bg-[#1A1A1A] hover:bg-[#27272A]'} transition-colors`}>
                      <div>
                        <span className="text-white font-medium">{a.name}</span>
                        <span className="text-[#A1A1AA] text-sm ml-2">— {a.age_class || 'Adulto'}, {a.weight}kg, {a.belt}</span>
                      </div>
                      {isEnrolled ? (
                        <span className="text-green-500 text-xs flex items-center gap-1"><CheckCircle size={16} /> Inscrito</span>
                      ) : (
                        <Button
                          size="sm"
                          disabled={enrolling === a._id}
                          onClick={() => handleEnroll(a)}
                          data-testid={`enroll-btn-${a._id}`}
                          className="bg-[#D4AF37] text-black hover:bg-[#E5C158]"
                        >
                          {enrolling === a._id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Inscrever'}
                        </Button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Bracket Athletes Popup */}
        <Dialog open={!!selectedBracketAthletes} onOpenChange={(open) => !open && setSelectedBracketAthletes(null)}>
          <DialogContent className="bg-[#141414] border-[#27272A] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white font-['Chivo']">{selectedBracketAthletes?.name}</DialogTitle>
            </DialogHeader>
            <div className="mt-2">
              <p className="text-[#71717A] text-sm mb-3">{selectedBracketAthletes?.athletes?.length || 0} atletas nesta chave</p>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto" data-testid="bracket-athletes-list">
                {selectedBracketAthletes?.athletes?.map((a, i) => (
                  <div key={a.id} className="flex items-center gap-3 p-3 rounded-lg bg-[#1A1A1A] border border-[#27272A]">
                    <div className="w-8 h-8 rounded-full bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] font-bold text-sm">
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-medium truncate flex items-center gap-2">
                        {a.name}
                        {a.manually_moved && (
                          <span
                            className="px-1.5 py-0.5 rounded text-micro font-bold bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40 print:hidden"
                            title={a.original_age_class ? `Original: ${a.original_age_class}` : 'Realocado manualmente'}
                            data-testid={`athlete-relocated-${a.id}`}
                          >
                            🔀 REALOCADO
                          </span>
                        )}
                      </div>
                      {(a.weight != null || a.weight_category) && (
                        <div className="text-xs text-[#A1A1AA] truncate">
                          {a.weight != null && <>{a.weight} kg</>}
                          {a.weight != null && a.weight_category && <span className="mx-1">·</span>}
                          {a.weight_category && <span className="text-[#D4AF37]">{a.weight_category}</span>}
                        </div>
                      )}
                    </div>
                    {!isAthlete && a.registration_id && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#D4AF37] hover:text-[#E5C158] hover:bg-[#D4AF37]/10 shrink-0"
                        onClick={() => setEditingAthlete(a)}
                        data-testid={`edit-athlete-weight-${a.id}`}
                        title="Editar peso"
                      >
                        <Pencil size={16} />
                      </Button>
                    )}
                  </div>
                ))}
                {(!selectedBracketAthletes?.athletes || selectedBracketAthletes.athletes.length === 0) && (
                  <p className="text-center text-[#71717A] py-4">Nenhum atleta encontrado</p>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Weight Dialog */}
        <EditWeightDialog
          open={!!editingAthlete}
          onClose={() => setEditingAthlete(null)}
          championshipId={id}
          championshipDate={championship?.date}
          categoryTableId={championship?.category_table_id}
          athleteSex={editingAthlete?.sex}
          currentAgeClass={editingAthlete?.age_class}
          registrationId={editingAthlete?.registration_id}
          athleteName={editingAthlete?.name || ''}
          currentWeight={editingAthlete?.weight}
          currentCategory={editingAthlete?.weight_category}
          onSaved={async () => {
            await reloadBracketsAndRegs();
            setSelectedBracketAthletes(null);
          }}
        />
      </div>
  );
}
